import { DragEvent, PointerEvent, useEffect, useRef, useState } from 'react'

import {
  AppId,
  CrossAppDndAction,
  CrossAppDndItemKind,
  canDropCrossAppPayload,
  hasCrossAppDndType,
  isHostPointerOverDesktopCrossAppDropSurface,
  readCrossAppDndPayload
} from '@inno-tools/container-bridge'
import { DEFAULT_SHORTCUT_ITEM_SIZE } from 'modules/desktop/constants'
import {
  getShortcutGroupCapabilities,
  isShortcutMemberLocked
} from 'modules/desktop/shortcutGroups'
import {
  BaseGridItem,
  DesktopGridItem,
  ShortcutGroup,
  ShortcutGroupBounds
} from 'shared/types/desktop-grid'

import { useSelectionStore } from '../../../stores'
import { getDragContext, setDragContext } from '../../../stores/dragStore'
import { useTouchEnvironment } from '../../../utils/useTouchEnvironment'
import { MAX_NEAREST_FREE_SEARCH_RADIUS } from '../DesktopGrid.constants'
import { CellSize, DragOffset, DropPreview } from '../types/grid.types'
import { findNearestFreePosition } from '../utils/findNearestFreePosition'
import { clearNonProfileMemory } from '../utils/reflowItemsForGridResize'
import { computeShortcutGroupTargets } from '../utils/shortcutDropLayout'
import {
  getShortcutGroupAtDropBounds,
  getShortcutGroupBoundaryCollisionItem,
  getShortcutGroupBounds,
  getShortcutGroupCollisionItems,
  getShortcutGroupInsertionPlan,
  getShortcutGroupPreviewBounds
} from '../utils/shortcutGroupBounds'
import { wouldCollide } from '../utils/wouldCollide'

const TOUCH_LONG_PRESS_MS = 500
const TOUCH_MOVE_CANCEL_THRESHOLD_PX = 10
export const INTERNAL_DESKTOP_DRAG_MIME =
  'application/x-container-desktop-internal-drag'

/** Synthetic move id: new shortcuts are not on the grid yet. */
const EXTERNAL_SHORTCUT_PLACEHOLDER_ID =
  '__external-files-to-desktop-shortcut__'

type TouchDragState = {
  itemId: string
  pointerId: number
  dragOffset: DragOffset
  container: HTMLElement | null
  element: HTMLElement | null
  started: boolean
}

type DragHandlerArgs = {
  itemId: string
  dragOffset: DragOffset
  nearestRow: number
  nearestCol: number
}

type GridPosition = { row: number; col: number }

type GroupTarget = {
  itemId: string
  position: GridPosition
  size: { width: number; height: number }
}

export const useDragAndDrop = (
  items: DesktopGridItem[],
  updateItems: (
    updater: (prevItems: DesktopGridItem[]) => DesktopGridItem[]
  ) => void,
  onExternalFilesDrop: (
    itemIds: number[],
    dropPosition: { row: number; col: number } | null
  ) => Promise<void> | void,
  onExternalFilesFolderDrop: (
    folderItemId: string,
    itemIds: number[]
  ) => Promise<void> | void,
  onFolderDrop: (
    folderItemId: string,
    draggedItemIds: string[]
  ) => Promise<void> | void,
  cellSize: CellSize,
  cols: number,
  rows: number,
  offset: { x: number; y: number },
  shortcutGroups: ShortcutGroup[] = [],
  onShortcutGroupDrop: (
    groupId: string,
    draggedItemIds: string[],
    dropPosition?: { row: number; col: number }
  ) => void = () => {},
  onShortcutGroupLeave: (draggedItemIds: string[]) => void = () => {}
) => {
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 })
  const [dropPreview, setDropPreview] = useState<DropPreview | null>(null)
  const [isExternalDragOver, setIsExternalDragOver] = useState(false)
  const [isTouchReorderActive, setIsTouchReorderActive] = useState(false)
  const [hoveredFolderItemId, setHoveredFolderItemId] = useState<string | null>(
    null
  )
  const [hoveredShortcutGroupId, setHoveredShortcutGroupId] = useState<
    string | null
  >(null)
  const [shortcutGroupBoundsPreview, setShortcutGroupBoundsPreview] = useState<{
    groupId: string
    bounds: ShortcutGroupBounds
  } | null>(null)
  // Preview rects for a multi-item drag. isValid is false when even one of the
  // items would land out of bounds or on top of a non-dragged item — the whole
  // group then can't be dropped.
  const [groupDropPreview, setGroupDropPreview] = useState<{
    items: DropPreview[]
    isValid: boolean
    groupId?: string
  } | null>(null)
  const isTouchEnvironment = useTouchEnvironment()
  const externalDragLeaveTimeoutRef = useRef<number | null>(null)
  // Last group positions that actually fit during the current multi-item drag.
  // While the cursor sits over an invalid spot, the preview sticks here (shown
  // red) and a drop snaps the group to it.
  const lastValidGroupTargetsRef = useRef<GroupTarget[] | null>(null)

  const longPressTimeoutRef = useRef<number | null>(null)
  const initialTouchPointRef = useRef<{ x: number; y: number } | null>(null)
  const touchStateRef = useRef<TouchDragState | null>(null)
  const touchMoveHandlerRef = useRef<((event: TouchEvent) => void) | null>(null)

  const getGroupBoundaryObstacles = (excludedGroupIds: string[] = []) =>
    getShortcutGroupCollisionItems(shortcutGroups, items, excludedGroupIds)

  const clearLongPressTimeout = () => {
    if (longPressTimeoutRef.current !== null) {
      window.clearTimeout(longPressTimeoutRef.current)
      longPressTimeoutRef.current = null
    }
  }

  const computeNearestFromPointer = (
    clientX: number,
    clientY: number,
    containerRect: DOMRect,
    scroll: { left: number; top: number }
  ) => {
    const { width: cellW, height: cellH, gapX, gapY } = cellSize
    const x = clientX - containerRect.left + scroll.left - offset.x
    const y = clientY - containerRect.top + scroll.top - offset.y

    const rawCol = Math.floor(x / (cellW + gapX))
    const rawRow = Math.floor(y / (cellH + gapY))

    return {
      nearestCol: Math.max(0, Math.min(cols - 1, rawCol)),
      nearestRow: Math.max(0, Math.min(rows - 1, rawRow))
    }
  }

  const getShortcutGroupTargetForDrag = (
    item: DesktopGridItem,
    currentDragOffset: DragOffset,
    nearestRow: number,
    nearestCol: number,
    excludedItemIds: string[] = []
  ) => {
    const projectedPosition = {
      row: Math.max(0, nearestRow - currentDragOffset.y),
      col: Math.max(0, nearestCol - currentDragOffset.x)
    }
    const droppableGroups = shortcutGroups.filter(
      group => getShortcutGroupCapabilities(group).canAcceptMembers
    )
    const target = getShortcutGroupAtDropBounds(
      droppableGroups,
      items,
      projectedPosition,
      item.size,
      excludedItemIds
    )

    const hoveredGroup = hoveredShortcutGroupId
      ? droppableGroups.find(group => group.id === hoveredShortcutGroupId)
      : undefined

    // Once the pointer has entered a group, keep that target forgiving enough
    // to reach a new outer row/column while the group grows. The relaxed
    // target is only available after the group was established as the active
    // target; a drag that starts outside still requires direct edge contact.
    const relaxedTarget = hoveredGroup
      ? getShortcutGroupAtDropBounds(
          [hoveredGroup],
          items,
          projectedPosition,
          item.size,
          excludedItemIds,
          1
        )
      : null
    const resolvedTarget = target ?? relaxedTarget
    const isRelaxedTarget = !target && !!relaxedTarget
    const isActiveHoveredTarget =
      !!target && target.id === hoveredShortcutGroupId

    if (!resolvedTarget || resolvedTarget.memberIds.includes(item.id)) {
      return resolvedTarget
    }

    // Touching a group's outer edge is also a valid standalone placement.
    // Prefer that placement when the projected rectangle is outside the
    // group's bounds and the normal collision resolver can keep it exactly at
    // the requested cell. Internal gaps still use the group insertion plan.
    const bounds = getShortcutGroupBounds(resolvedTarget, items)
    if (!bounds) return resolvedTarget

    const projectedRight = projectedPosition.col + item.size.width
    const projectedBottom = projectedPosition.row + item.size.height
    const overlapsBounds =
      projectedPosition.col < bounds.position.col + bounds.size.width &&
      projectedRight > bounds.position.col &&
      projectedPosition.row < bounds.position.row + bounds.size.height &&
      projectedBottom > bounds.position.row

    if (overlapsBounds || isRelaxedTarget || isActiveHoveredTarget) {
      return resolvedTarget
    }

    const standaloneTarget = computeTargetPosition({
      itemId: item.id,
      dragOffset: currentDragOffset,
      nearestRow,
      nearestCol
    })
    if (
      standaloneTarget?.row === projectedPosition.row &&
      standaloneTarget.col === projectedPosition.col
    ) {
      return null
    }

    return resolvedTarget
  }

  /**
   * clamps the preferred cell to grid bounds and falls back to the nearest
   * free spot on collision. itemsForNearestFree excludes the moving item;
   * itemsForCollision only skips it by id.
   */
  const resolveDropTarget = (
    preferred: GridPosition,
    size: { width: number; height: number },
    skipId: string | undefined,
    itemsForCollision: BaseGridItem[],
    itemsForNearestFree: BaseGridItem[]
  ): GridPosition | null => {
    const finalRow = Math.min(
      Math.max(0, preferred.row),
      Math.max(0, rows - size.height)
    )
    const finalCol = Math.min(
      Math.max(0, preferred.col),
      Math.max(0, cols - size.width)
    )
    const candidate = { row: finalRow, col: finalCol }

    const hasCollision = wouldCollide(
      skipId ?? '',
      candidate,
      size,
      itemsForCollision
    )
    if (!hasCollision) return candidate

    return findNearestFreePosition(
      itemsForNearestFree,
      cols,
      rows,
      size,
      candidate,
      skipId,
      MAX_NEAREST_FREE_SEARCH_RADIUS
    )
  }

  const computeTargetPosition = ({
    itemId,
    dragOffset: currentDragOffset,
    nearestRow,
    nearestCol
  }: DragHandlerArgs) => {
    const item = items.find(i => i.id === itemId)
    if (!item || item.isPinned) return null

    const preferred = {
      row: Math.max(0, nearestRow - currentDragOffset.y),
      col: Math.max(0, nearestCol - currentDragOffset.x)
    }
    const sourceGroupIds = shortcutGroups
      .filter(group => group.memberIds.includes(itemId))
      .map(group => group.id)
    const groupBoundaryObstacles = getGroupBoundaryObstacles(sourceGroupIds)

    return resolveDropTarget(
      preferred,
      item.size,
      itemId,
      [...items, ...groupBoundaryObstacles],
      [...items.filter(i => i.id !== itemId), ...groupBoundaryObstacles]
    )
  }

  const computeExternalShortcutTarget = (
    preferred: GridPosition
  ): GridPosition | null =>
    resolveDropTarget(
      preferred,
      DEFAULT_SHORTCUT_ITEM_SIZE,
      EXTERNAL_SHORTCUT_PLACEHOLDER_ID,
      [...items, ...getGroupBoundaryObstacles()],
      [...items, ...getGroupBoundaryObstacles()]
    )

  /**
   * Drop slots for a multi-file drag preview. Delegates to the shared layout
   * (tidy block, edge-clamped, kept-together when possible, gap-packed when
   * crowded) so the preview matches exactly where useMoveFilesItemsToDesktop
   * lands the files.
   */
  const computeExternalShortcutTargets = (
    preferred: GridPosition,
    count: number
  ): GridPosition[] =>
    computeShortcutGroupTargets(
      preferred,
      count,
      cols,
      rows,
      [...items, ...getGroupBoundaryObstacles()],
      DEFAULT_SHORTCUT_ITEM_SIZE
    )

  const computeExternalDropPositionFromPointer = ({
    clientX,
    clientY,
    container
  }: {
    clientX: number
    clientY: number
    container: HTMLDivElement | null
  }): GridPosition | null => {
    if (!container) return null

    const rect = container.getBoundingClientRect()
    const isInside =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    if (!isInside) return null

    const { nearestRow, nearestCol } = computeNearestFromPointer(
      clientX,
      clientY,
      rect,
      {
        left: container.scrollLeft ?? 0,
        top: container.scrollTop ?? 0
      }
    )

    return computeExternalShortcutTarget({ row: nearestRow, col: nearestCol })
  }

  /**
   * Folder under the pointer during an incoming files drag, if any. Lets a
   * cross-app drop land its files inside a desktop folder instead of as
   * shortcuts on the grid — mirroring the intra-desktop folder drop.
   */
  const getExternalFolderTargetAtPointer = (
    clientX: number,
    clientY: number,
    container: HTMLDivElement | null
  ): string | null => {
    if (!container) return null

    const rect = container.getBoundingClientRect()
    const isInside =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    if (!isInside) return null

    const { nearestRow, nearestCol } = computeNearestFromPointer(
      clientX,
      clientY,
      rect,
      {
        left: container.scrollLeft ?? 0,
        top: container.scrollTop ?? 0
      }
    )

    return findFolderItemAtCell(nearestRow, nearestCol, [])
  }

  const findFolderItemAtCell = (
    row: number,
    col: number,
    excludeIds: string[]
  ): string | null => {
    const exclude = new Set(excludeIds)
    const folder = items.find(item => {
      if (item.itemType !== 'shortcut' || item.fileType !== 'folder') {
        return false
      }
      if (exclude.has(item.id)) return false
      const { row: r, col: c } = item.position
      const { width: w, height: h } = item.size
      return col >= c && col < c + w && row >= r && row < r + h
    })
    return folder?.id ?? null
  }

  // only shortcuts can move into a folder.
  const isShortcutItemId = (id: string) => {
    const item = items.find(candidate => candidate.id === id)
    return item?.itemType === 'shortcut' && !item.isPinned
  }

  const applyMoveIfNeeded = (
    itemId: string,
    target: { row: number; col: number } | null
  ) => {
    const item = items.find(i => i.id === itemId)
    if (!item || item.isPinned || !target) return

    if (target.row === item.position.row && target.col === item.position.col) {
      return
    }

    clearNonProfileMemory()
    updateItems(prevItems =>
      prevItems.map(it =>
        it.id === itemId
          ? {
              ...it,
              position: { row: target.row, col: target.col }
            }
          : it
      )
    )
  }

  /**
   * Translate every dragged item by the same delta as the grabbed anchor and
   * report whether the whole group fits. The group moves as a rigid block:
   * relative offsets are preserved and there's no per-item nudging — if a
   * single item would fall off the grid or overlap a non-dragged item, the
   * entire drop is invalid.
   */
  const computeGroupMove = ({
    anchorId,
    itemIds,
    dragOffset: currentDragOffset,
    nearestRow,
    nearestCol,
    groupId
  }: {
    anchorId: string
    itemIds: string[]
    dragOffset: DragOffset
    nearestRow: number
    nearestCol: number
    groupId?: string
  }): { targets: GroupTarget[]; isValid: boolean } | null => {
    if (
      groupId &&
      !getShortcutGroupCapabilities(
        shortcutGroups.find(group => group.id === groupId) ?? groupId
      ).canMove
    ) {
      return null
    }
    const anchor = items.find(i => i.id === anchorId)
    if (!anchor) return null

    const dragged = itemIds
      .map(id => items.find(i => i.id === id))
      .filter(
        (item): item is DesktopGridItem => item !== undefined && !item.isPinned
      )
    if (!dragged.length) return null

    const groupBounds = groupId
      ? getShortcutGroupBounds(
          { id: groupId, title: '', memberIds: itemIds, createdAt: new Date() },
          items
        )
      : null
    const movingPosition = groupBounds?.position ?? anchor.position
    const movingSize = groupBounds?.size ?? anchor.size

    // A formal group's title bar renders one row above its members, so the
    // top row is reserved and members can never sit at row 0.
    const minRow = groupId ? 1 : 0
    const preferredRow = Math.max(minRow, nearestRow - currentDragOffset.y)
    const preferredCol = Math.max(0, nearestCol - currentDragOffset.x)
    const anchorRow = Math.min(
      preferredRow,
      Math.max(minRow, rows - movingSize.height)
    )
    const anchorCol = Math.min(
      preferredCol,
      Math.max(0, cols - movingSize.width)
    )

    const deltaRow = anchorRow - movingPosition.row
    const deltaCol = anchorCol - movingPosition.col

    const targets: GroupTarget[] = dragged.map(item => ({
      itemId: item.id,
      size: { ...item.size },
      position: {
        row: item.position.row + deltaRow,
        col: item.position.col + deltaCol
      }
    }))

    const draggedIds = new Set(dragged.map(item => item.id))
    const excludedGroupIds = shortcutGroups
      .filter(
        group =>
          group.id === groupId ||
          group.memberIds.every(memberId => draggedIds.has(memberId))
      )
      .map(group => group.id)
    const others = [
      ...items.filter(i => !draggedIds.has(i.id)),
      ...getGroupBoundaryObstacles(excludedGroupIds)
    ]
    const projectedBounds = getShortcutGroupPreviewBounds(
      targets.map(target => ({
        position: target.position,
        size: target.size
      }))
    )
    const projectedBoundary = projectedBounds
      ? getShortcutGroupBoundaryCollisionItem(
          groupId ?? '__dragged-shortcut-group',
          projectedBounds
        )
      : null

    const isValid =
      (!projectedBoundary ||
        !wouldCollide(
          projectedBoundary.id,
          projectedBoundary.position,
          projectedBoundary.size,
          others
        )) &&
      targets.every(t => {
        if (t.position.row < 0 || t.position.col < 0) return false
        if (t.position.row + t.size.height > rows) return false
        if (t.position.col + t.size.width > cols) return false
        return !wouldCollide(t.itemId, t.position, t.size, others)
      })

    return { targets, isValid }
  }

  const applyGroupMove = (targets: GroupTarget[]) => {
    clearNonProfileMemory()
    const next = new Map(targets.map(t => [t.itemId, t.position]))
    updateItems(prevItems =>
      prevItems.map(it =>
        next.has(it.id) && !it.isPinned
          ? { ...it, position: { ...next.get(it.id)! } }
          : it
      )
    )
  }

  const moveShortcutGroupBy = (
    groupId: string,
    delta: { row: number; col: number }
  ) => {
    const group = shortcutGroups.find(candidate => candidate.id === groupId)
    if (!group) return
    if (!getShortcutGroupCapabilities(group).canMove) return

    const dragged = group.memberIds
      .map(id => items.find(item => item.id === id))
      .filter(
        (item): item is DesktopGridItem =>
          item !== undefined && item.itemType === 'shortcut' && !item.isPinned
      )
    if (!dragged.length) return

    const targets: GroupTarget[] = dragged.map(item => ({
      itemId: item.id,
      size: { ...item.size },
      position: {
        row: item.position.row + delta.row,
        col: item.position.col + delta.col
      }
    }))
    const draggedIds = new Set(dragged.map(item => item.id))
    const others = [
      ...items.filter(item => !draggedIds.has(item.id)),
      ...getGroupBoundaryObstacles([groupId])
    ]
    const projectedBounds = getShortcutGroupPreviewBounds(
      targets.map(target => ({
        position: target.position,
        size: target.size
      }))
    )
    const projectedBoundary = projectedBounds
      ? getShortcutGroupBoundaryCollisionItem(groupId, projectedBounds)
      : null
    const isValid =
      // The title bar renders one row above the members, so row 0 is reserved.
      (projectedBounds ? projectedBounds.position.row >= 1 : true) &&
      (!projectedBoundary ||
        !wouldCollide(
          projectedBoundary.id,
          projectedBoundary.position,
          projectedBoundary.size,
          others
        )) &&
      targets.every(target => {
        if (target.position.row < 0 || target.position.col < 0) return false
        if (target.position.row + target.size.height > rows) return false
        if (target.position.col + target.size.width > cols) return false
        return !wouldCollide(
          target.itemId,
          target.position,
          target.size,
          others
        )
      })
    if (isValid) applyGroupMove(targets)
  }

  const resetGridDragState = () => {
    setActiveItemId(null)
    setDropPreview(null)
    setGroupDropPreview(null)
    setHoveredFolderItemId(null)
    setHoveredShortcutGroupId(null)
    setShortcutGroupBoundsPreview(null)
    setDragContext(null)
    lastValidGroupTargetsRef.current = null
  }

  const updateShortcutGroupBoundsPreview = (
    groupId: string,
    plan: {
      target: { row: number; col: number }
      members: DesktopGridItem[]
    } | null,
    item: DesktopGridItem
  ) => {
    const bounds = plan
      ? getShortcutGroupPreviewBounds([
          ...plan.members,
          { position: plan.target, size: item.size }
        ])
      : null
    setShortcutGroupBoundsPreview(bounds ? { groupId, bounds } : null)
  }

  useEffect(() => {
    if (hoveredShortcutGroupId === null) {
      setShortcutGroupBoundsPreview(null)
    }
  }, [hoveredShortcutGroupId])

  const handleDragStart =
    (itemId: string) => (e: DragEvent<HTMLDivElement>) => {
      const item = items.find(candidate => candidate.id === itemId)
      const isLockedMember = isShortcutMemberLocked(shortcutGroups, itemId)
      if (item?.isPinned || isLockedMember) {
        e.preventDefault()
        return
      }
      setActiveItemId(itemId)
      e.dataTransfer.setData('text/plain', itemId)
      e.dataTransfer.setData(INTERNAL_DESKTOP_DRAG_MIME, '1')
      const rect = e.currentTarget.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      const offsetY = e.clientY - rect.top
      const cellOffsetX = Math.floor(offsetX / (cellSize.width + cellSize.gapX))
      const cellOffsetY = Math.floor(
        offsetY / (cellSize.height + cellSize.gapY)
      )
      const dragOffsetValue = { x: cellOffsetX, y: cellOffsetY }
      setDragOffset(dragOffsetValue)
      if (item) {
        const { selectedIds } = useSelectionStore.getState()
        // Drag the whole selection if this item is in it, else just this one.
        // Dragging never mutates the selection (that's click/marquee only).
        const itemIds = selectedIds.has(itemId)
          ? Array.from(selectedIds).filter(id => {
              if (items.find(candidate => candidate.id === id)?.isPinned) {
                return false
              }
              return !isShortcutMemberLocked(shortcutGroups, id)
            })
          : [itemId]
        // Seed the fallback with the items' current (valid) positions so an
        // invalid hover always has somewhere to stick.
        lastValidGroupTargetsRef.current =
          itemIds.length > 1
            ? items
                .filter(i => itemIds.includes(i.id))
                .map(i => ({
                  itemId: i.id,
                  position: { ...i.position },
                  size: { ...i.size }
                }))
            : null
        if (itemIds.length > 1) {
          const dragImageContainer = document.createElement('div')
          dragImageContainer.className = 'dg-drag-image'
          const badge = document.createElement('div')
          badge.className = 'dg-drag-badge'
          badge.textContent = String(itemIds.length)
          dragImageContainer.appendChild(badge)
          document.body.appendChild(dragImageContainer)
          e.dataTransfer.setDragImage(dragImageContainer, 0, 0)
          requestAnimationFrame(() => dragImageContainer.remove())
        }
        setDragContext({
          source: 'grid',
          itemId,
          itemIds,
          size: { ...item.size },
          dragOffset: dragOffsetValue
        })
      }
    }

  const handleShortcutGroupDragStart =
    (groupId: string) => (e: DragEvent<HTMLElement>) => {
      const group = shortcutGroups.find(candidate => candidate.id === groupId)
      if (!group || !getShortcutGroupCapabilities(group).canMove) {
        e.preventDefault()
        return
      }
      const memberItems = group?.memberIds
        .map(id => items.find(item => item.id === id))
        .filter(
          (item): item is DesktopGridItem =>
            item !== undefined && item.itemType === 'shortcut' && !item.isPinned
        )
      const bounds = group ? getShortcutGroupBounds(group, items) : null
      if (!memberItems?.length || !bounds) {
        e.preventDefault()
        return
      }

      const anchor = memberItems.reduce((current, item) => {
        if (item.position.row < current.position.row) return item
        if (
          item.position.row === current.position.row &&
          item.position.col < current.position.col
        ) {
          return item
        }
        return current
      })
      // The handle is fixed to the group's top-right corner, so the preview
      // must preserve that exact corner-to-cursor relationship regardless of
      // native drag event coordinates.
      const dragOffsetValue = {
        x: Math.max(0, bounds.size.width - 1),
        y: 0
      }

      setActiveItemId(anchor.id)
      setDragOffset(dragOffsetValue)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', anchor.id)
      e.dataTransfer.setData(INTERNAL_DESKTOP_DRAG_MIME, '1')
      lastValidGroupTargetsRef.current = memberItems.map(item => ({
        itemId: item.id,
        position: { ...item.position },
        size: { ...item.size }
      }))
      setDragContext({
        source: 'grid',
        dragType: 'shortcut-group',
        groupId,
        itemId: anchor.id,
        itemIds: memberItems.map(item => item.id),
        size: { ...anchor.size },
        dragOffset: dragOffsetValue
      })
    }

  const handleContainerDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()

    const containerRect = e.currentTarget.getBoundingClientRect()
    const scrollOffsets = {
      left: e.currentTarget.scrollLeft ?? 0,
      top: e.currentTarget.scrollTop ?? 0
    }
    const isInternalDesktopDrag = Boolean(
      e.dataTransfer?.types.includes(INTERNAL_DESKTOP_DRAG_MIME)
    )

    if (!isInternalDesktopDrag && hasCrossAppDndType(e.dataTransfer)) {
      const payload = readCrossAppDndPayload(e.dataTransfer)
      if (
        canDropCrossAppPayload(payload, {
          sourceApps: [AppId.CLUSTER_FILES],
          itemKinds: [CrossAppDndItemKind.CLUSTER_FILES_ITEM],
          actions: [CrossAppDndAction.MOVE]
        }) &&
        payload &&
        payload.itemIds.length
      ) {
        const { nearestRow: dropRow, nearestCol: dropCol } =
          computeNearestFromPointer(
            e.clientX,
            e.clientY,
            containerRect,
            scrollOffsets
          )
        // A folder under the cursor takes the files inside it; otherwise they
        // land as shortcuts on the grid.
        const folderTargetId = findFolderItemAtCell(dropRow, dropCol, [])
        if (folderTargetId) {
          void onExternalFilesFolderDrop(folderTargetId, payload.itemIds)
        } else {
          void onExternalFilesDrop(payload.itemIds, {
            row: dropRow,
            col: dropCol
          })
        }
      }
      setActiveItemId(null)
      setDropPreview(null)
      setGroupDropPreview(null)
      setDragContext(null)
      setIsExternalDragOver(false)
      setHoveredFolderItemId(null)
      setHoveredShortcutGroupId(null)
      if (externalDragLeaveTimeoutRef.current !== null) {
        window.clearTimeout(externalDragLeaveTimeoutRef.current)
        externalDragLeaveTimeoutRef.current = null
      }
      return
    }

    const ctx = getDragContext()
    const { nearestRow, nearestCol } = computeNearestFromPointer(
      e.clientX,
      e.clientY,
      containerRect,
      scrollOffsets
    )
    if (ctx?.source === 'grid') {
      if (ctx.dragType !== 'shortcut-group') {
        const draggedItem = items.find(item => item.id === ctx.itemId)
        const folderTargetId = findFolderItemAtCell(
          nearestRow,
          nearestCol,
          ctx.itemIds
        )
        if (folderTargetId) {
          void onFolderDrop(folderTargetId, ctx.itemIds)
          resetGridDragState()
          return
        }

        const shortcutGroupTarget = draggedItem
          ? getShortcutGroupTargetForDrag(
              draggedItem,
              ctx.dragOffset,
              nearestRow,
              nearestCol
            )
          : null
        if (shortcutGroupTarget) {
          onShortcutGroupDrop(shortcutGroupTarget.id, ctx.itemIds, {
            row: nearestRow,
            col: nearestCol
          })
          resetGridDragState()
          return
        }
      }

      if (ctx.dragType === 'shortcut-group' || ctx.itemIds.length > 1) {
        if (ctx.dragType !== 'shortcut-group') {
          onShortcutGroupLeave(ctx.itemIds)
        }
        const plan = computeGroupMove({
          anchorId: ctx.itemId,
          itemIds: ctx.itemIds,
          dragOffset: ctx.dragOffset,
          nearestRow,
          nearestCol,
          groupId: ctx.dragType === 'shortcut-group' ? ctx.groupId : undefined
        })
        // Land on the live target when it fits, otherwise snap to the last
        // fitting position the preview was pinned to.
        const targets = plan?.isValid
          ? plan.targets
          : lastValidGroupTargetsRef.current
        if (targets) applyGroupMove(targets)
        resetGridDragState()
        return
      }

      const target = computeTargetPosition({
        itemId: ctx.itemId,
        dragOffset: ctx.dragOffset,
        nearestRow,
        nearestCol
      })

      onShortcutGroupLeave(ctx.itemIds)
      applyMoveIfNeeded(ctx.itemId, target)
      resetGridDragState()
      return
    }

    const itemId = activeItemId || e.dataTransfer.getData('text/plain')
    if (!itemId) return

    const folderTargetId = findFolderItemAtCell(nearestRow, nearestCol, [
      itemId
    ])
    if (folderTargetId && isShortcutItemId(itemId)) {
      void onFolderDrop(folderTargetId, [itemId])
      resetGridDragState()
      return
    }

    const draggedItem = items.find(item => item.id === itemId)
    const shortcutGroupTarget = draggedItem
      ? getShortcutGroupTargetForDrag(
          draggedItem,
          dragOffset,
          nearestRow,
          nearestCol
        )
      : null
    if (shortcutGroupTarget && isShortcutItemId(itemId)) {
      onShortcutGroupDrop(shortcutGroupTarget.id, [itemId], {
        row: nearestRow,
        col: nearestCol
      })
      resetGridDragState()
      return
    }

    const target = computeTargetPosition({
      itemId,
      dragOffset,
      nearestRow,
      nearestCol
    })

    onShortcutGroupLeave([itemId])
    applyMoveIfNeeded(itemId, target)
    resetGridDragState()
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    const isInternalDesktopDrag = Boolean(
      e.dataTransfer?.types.includes(INTERNAL_DESKTOP_DRAG_MIME)
    )

    if (!isInternalDesktopDrag && hasCrossAppDndType(e.dataTransfer)) {
      const payload = readCrossAppDndPayload(e.dataTransfer)
      if (
        !canDropCrossAppPayload(payload, {
          sourceApps: [AppId.CLUSTER_FILES],
          itemKinds: [CrossAppDndItemKind.CLUSTER_FILES_ITEM],
          actions: [CrossAppDndAction.MOVE]
        })
      ) {
        setIsExternalDragOver(false)
        return
      }
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDropPreview(null)
      // Show a desktop-wide highlight while a foreign drag hovers over us.
      // dragleave fires for every child boundary crossed, so we debounce
      // turning the highlight off — see handleDragLeave below.
      if (externalDragLeaveTimeoutRef.current !== null) {
        window.clearTimeout(externalDragLeaveTimeoutRef.current)
        externalDragLeaveTimeoutRef.current = null
      }
      setIsExternalDragOver(true)
      return
    }

    e.preventDefault()
    if (externalDragLeaveTimeoutRef.current !== null) {
      window.clearTimeout(externalDragLeaveTimeoutRef.current)
      externalDragLeaveTimeoutRef.current = null
    }
    setIsExternalDragOver(false)
    const containerRect = e.currentTarget.getBoundingClientRect()
    const scrollOffsets = {
      left: e.currentTarget.scrollLeft ?? 0,
      top: e.currentTarget.scrollTop ?? 0
    }
    const { nearestRow, nearestCol } = computeNearestFromPointer(
      e.clientX,
      e.clientY,
      containerRect,
      scrollOffsets
    )
    let ctx = getDragContext()

    if ((!ctx || ctx.source !== 'grid') && activeItemId) {
      const item = items.find(i => i.id === activeItemId)
      if (item) {
        ctx = {
          source: 'grid',
          itemId: activeItemId,
          itemIds: [activeItemId],
          size: { ...item.size },
          dragOffset: { ...dragOffset }
        }
        setDragContext(ctx)
      }
    }

    if (!ctx || ctx.source !== 'grid') {
      setDropPreview(null)
      setGroupDropPreview(null)
      setHoveredFolderItemId(null)
      setHoveredShortcutGroupId(null)
      return
    }

    const item = items.find(i => i.id === ctx.itemId)
    if (!item) {
      setDropPreview(null)
      setGroupDropPreview(null)
      setHoveredFolderItemId(null)
      setHoveredShortcutGroupId(null)
      return
    }

    // If pointer is over a folder that isn't being dragged, highlight it as a
    // move-into target and suppress the reposition preview.
    if (ctx.dragType !== 'shortcut-group') {
      const folderTargetId = findFolderItemAtCell(
        nearestRow,
        nearestCol,
        ctx.itemIds
      )
      if (folderTargetId) {
        setHoveredFolderItemId(folderTargetId)
        setHoveredShortcutGroupId(null)
        setDropPreview(null)
        setGroupDropPreview(null)
        return
      }

      const shortcutGroupTarget = getShortcutGroupTargetForDrag(
        item,
        ctx.dragOffset,
        nearestRow,
        nearestCol
      )
      if (shortcutGroupTarget) {
        setHoveredShortcutGroupId(shortcutGroupTarget.id)
        if (ctx.itemIds.length === 1 && item.itemType === 'shortcut') {
          const groupMembers = shortcutGroupTarget.memberIds
            .map(id => items.find(candidate => candidate.id === id))
            .filter(
              (candidate): candidate is DesktopGridItem =>
                !!candidate && !ctx.itemIds.includes(candidate.id)
            )
          const excludedGroupIds = shortcutGroups
            .filter(
              group =>
                group.id === shortcutGroupTarget.id ||
                group.memberIds.some(memberId => ctx.itemIds.includes(memberId))
            )
            .map(group => group.id)
          const insertionPlan = getShortcutGroupInsertionPlan({
            members: groupMembers,
            item,
            occupied: [
              ...items.filter(candidate => !ctx.itemIds.includes(candidate.id)),
              ...getGroupBoundaryObstacles(excludedGroupIds)
            ],
            cols,
            rows,
            dropPosition: { row: nearestRow, col: nearestCol },
            reorderItemId: shortcutGroupTarget.memberIds.includes(item.id)
              ? item.id
              : undefined,
            reorderItemPosition: item.position
          })
          updateShortcutGroupBoundsPreview(
            shortcutGroupTarget.id,
            insertionPlan,
            item
          )
          setDropPreview(
            insertionPlan
              ? {
                  position: insertionPlan.target,
                  size: { ...item.size },
                  itemId: item.id
                }
              : null
          )
        } else {
          setDropPreview(null)
        }
        setGroupDropPreview(null)
        return
      }
    }
    setHoveredShortcutGroupId(null)
    setHoveredFolderItemId(null)

    // Multi-item drag: while the whole group fits, preview at the live target
    // and remember it. Over an invalid spot, keep the preview pinned to that
    // last fitting position but tint it red — a drop snaps the group there.
    if (ctx.dragType === 'shortcut-group' || ctx.itemIds.length > 1) {
      const plan = computeGroupMove({
        anchorId: ctx.itemId,
        itemIds: ctx.itemIds,
        dragOffset: ctx.dragOffset,
        nearestRow,
        nearestCol,
        groupId: ctx.dragType === 'shortcut-group' ? ctx.groupId : undefined
      })
      setDropPreview(null)

      if (plan?.isValid) {
        lastValidGroupTargetsRef.current = plan.targets
        setGroupDropPreview({
          items: plan.targets.map(t => ({
            position: t.position,
            size: t.size,
            itemId: t.itemId
          })),
          isValid: true,
          ...(ctx.dragType === 'shortcut-group' ? { groupId: ctx.groupId } : {})
        })
        return
      }

      const fallback = lastValidGroupTargetsRef.current
      setGroupDropPreview(
        fallback
          ? {
              items: fallback.map(t => ({
                position: t.position,
                size: t.size,
                itemId: t.itemId
              })),
              isValid: false,
              ...(ctx.dragType === 'shortcut-group'
                ? { groupId: ctx.groupId }
                : {})
            }
          : null
      )
      return
    }

    setGroupDropPreview(null)
    const target = computeTargetPosition({
      itemId: ctx.itemId,
      dragOffset: ctx.dragOffset,
      nearestRow,
      nearestCol
    })

    setDropPreview(target ? { position: target, size: { ...item.size } } : null)
  }

  const handleDragEnd = () => {
    setActiveItemId(null)
    setDropPreview(null)
    setGroupDropPreview(null)
    setDragContext(null)
    setIsTouchReorderActive(false)
    setIsExternalDragOver(false)
    setHoveredFolderItemId(null)
    setHoveredShortcutGroupId(null)
    lastValidGroupTargetsRef.current = null
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (getDragContext()?.source === 'marketplace') {
      const nextTarget = e.relatedTarget
      if (nextTarget instanceof Node && e.currentTarget.contains(nextTarget)) {
        return
      }

      setDropPreview(null)
      setGroupDropPreview(null)
      setHoveredFolderItemId(null)
      return
    }

    if (!hasCrossAppDndType(e.dataTransfer)) return

    // Ignore child-boundary transitions: keep highlight while pointer is still
    // effectively over the desktop drop surface.
    if (
      isHostPointerOverDesktopCrossAppDropSurface(
        e.currentTarget,
        e.clientX,
        e.clientY
      )
    ) {
      return
    }

    if (externalDragLeaveTimeoutRef.current !== null) {
      window.clearTimeout(externalDragLeaveTimeoutRef.current)
      externalDragLeaveTimeoutRef.current = null
    }
    setIsExternalDragOver(false)
  }

  const handleExternalPointerPreview = ({
    phase,
    clientX,
    clientY,
    container,
    itemCount = 1
  }: {
    phase: 'start' | 'move' | 'end' | 'cancel'
    clientX: number
    clientY: number
    container: HTMLDivElement | null
    itemCount?: number
  }) => {
    const clearPreviews = () => {
      setDropPreview(null)
      setGroupDropPreview(null)
      setHoveredFolderItemId(null)
      setHoveredShortcutGroupId(null)
    }

    if (!container || phase === 'end' || phase === 'cancel') {
      clearPreviews()
      return
    }
    if (
      !isHostPointerOverDesktopCrossAppDropSurface(container, clientX, clientY)
    ) {
      clearPreviews()
      return
    }

    // Over a folder: highlight it as the drop target and drop the slot preview,
    // since the files will move into the folder rather than onto the grid.
    const folderTargetId = getExternalFolderTargetAtPointer(
      clientX,
      clientY,
      container
    )
    if (folderTargetId) {
      setHoveredFolderItemId(folderTargetId)
      setHoveredShortcutGroupId(null)
      setDropPreview(null)
      setGroupDropPreview(null)
      return
    }
    setHoveredFolderItemId(null)
    setHoveredShortcutGroupId(null)

    const target = computeExternalDropPositionFromPointer({
      clientX,
      clientY,
      container
    })
    if (!target) {
      setDropPreview(null)
      setGroupDropPreview(null)
      return
    }

    // Several incoming files: preview the whole group at the cells they'll
    // occupy. The group is always droppable here (each shortcut is placed in
    // the nearest free slot), so it never tints red.
    if (itemCount > 1) {
      setDropPreview(null)
      const targets = computeExternalShortcutTargets(target, itemCount)
      setGroupDropPreview({
        items: targets.map(position => ({
          position,
          size: DEFAULT_SHORTCUT_ITEM_SIZE
        })),
        isValid: true
      })
      return
    }

    setGroupDropPreview(null)
    setDropPreview({ position: target, size: DEFAULT_SHORTCUT_ITEM_SIZE })
  }

  const enableTouchMovePrevention = () => {
    if (touchMoveHandlerRef.current) return
    const handler = (event: TouchEvent) => {
      if (touchStateRef.current?.started) {
        event.preventDefault()
      }
    }
    touchMoveHandlerRef.current = handler
    window.addEventListener('touchmove', handler, { passive: false })
  }

  const disableTouchMovePrevention = () => {
    if (!touchMoveHandlerRef.current) return
    window.removeEventListener('touchmove', touchMoveHandlerRef.current)
    touchMoveHandlerRef.current = null
  }

  useEffect(() => {
    return () => {
      disableTouchMovePrevention()
      clearLongPressTimeout()
      if (externalDragLeaveTimeoutRef.current !== null) {
        window.clearTimeout(externalDragLeaveTimeoutRef.current)
        externalDragLeaveTimeoutRef.current = null
      }
    }
  }, [])

  const finalizeTouchDrag = (opts?: {
    resetPreview?: boolean
    pointerId?: number
  }) => {
    const state = touchStateRef.current
    if (state?.element && typeof state.pointerId === 'number') {
      state.element.style.touchAction = ''
      try {
        state.element.releasePointerCapture(state.pointerId)
      } catch {
        // capture may already be released
      }
    }
    clearLongPressTimeout()
    touchStateRef.current = null
    initialTouchPointRef.current = null
    disableTouchMovePrevention()
    setActiveItemId(null)
    if (opts?.resetPreview ?? true) {
      setDropPreview(null)
    }
    setGroupDropPreview(null)
    setDragContext(null)
    setIsTouchReorderActive(false)
    setHoveredFolderItemId(null)
    setHoveredShortcutGroupId(null)
  }

  const handleTouchPointerDown =
    (itemId: string) => (event: PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== 'touch') return
      if (items.find(item => item.id === itemId)?.isPinned) return
      if (isShortcutMemberLocked(shortcutGroups, itemId)) {
        return
      }

      const current = touchStateRef.current
      if (current && current.started) return
      if (current && current.pointerId !== event.pointerId) return

      const container = event.currentTarget.closest(
        '[data-desktop-grid]'
      ) as HTMLElement | null

      const rect = event.currentTarget.getBoundingClientRect()
      const offsetX = event.clientX - rect.left
      const offsetY = event.clientY - rect.top
      const cellOffsetX = Math.floor(offsetX / (cellSize.width + cellSize.gapX))
      const cellOffsetY = Math.floor(
        offsetY / (cellSize.height + cellSize.gapY)
      )

      touchStateRef.current = {
        itemId,
        pointerId: event.pointerId,
        dragOffset: { x: cellOffsetX, y: cellOffsetY },
        container,
        element: event.currentTarget as HTMLElement,
        started: false
      }
      initialTouchPointRef.current = { x: event.clientX, y: event.clientY }
      clearLongPressTimeout()
      longPressTimeoutRef.current = window.setTimeout(() => {
        const state = touchStateRef.current
        if (!state) return
        const item = items.find(i => i.id === state.itemId)
        if (!item) {
          finalizeTouchDrag({ resetPreview: true })
          return
        }

        enableTouchMovePrevention()
        if (state.element) {
          state.element.style.touchAction = 'none'
        }

        try {
          state.element?.setPointerCapture(state.pointerId)
        } catch {
          // pointer may no longer be active
        }
        setActiveItemId(state.itemId)
        setDragOffset({ ...state.dragOffset })
        setDragContext({
          source: 'grid',
          itemId: state.itemId,
          itemIds: [state.itemId],
          size: { ...item.size },
          dragOffset: { ...state.dragOffset }
        })
        touchStateRef.current = { ...state, started: true }
        setIsTouchReorderActive(true)
        setDropPreview({
          position: { ...item.position },
          size: { ...item.size }
        })
      }, TOUCH_LONG_PRESS_MS)
    }

  const handleTouchPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') return
    const state = touchStateRef.current
    if (!state || state.pointerId !== event.pointerId) return

    if (!state.started) {
      const startPoint = initialTouchPointRef.current
      if (startPoint) {
        const deltaX = event.clientX - startPoint.x
        const deltaY = event.clientY - startPoint.y
        const distance = Math.hypot(deltaX, deltaY)
        if (distance > TOUCH_MOVE_CANCEL_THRESHOLD_PX) {
          finalizeTouchDrag({ resetPreview: false })
        }
      }
      return
    }

    event.preventDefault()
    if (state.element) {
      state.element.style.touchAction = 'none'
      try {
        if (!state.element.hasPointerCapture(state.pointerId)) {
          state.element.setPointerCapture(state.pointerId)
        }
      } catch {
        // ignoring because maybe pointer was released externally
      }
    }
    const item = items.find(i => i.id === state.itemId)
    if (!item) {
      finalizeTouchDrag({ resetPreview: true })
      return
    }

    const containerRect = state.container?.getBoundingClientRect()
    if (!containerRect) return
    const scrollOffsets = {
      left: state.container?.scrollLeft ?? 0,
      top: state.container?.scrollTop ?? 0
    }

    const { nearestRow, nearestCol } = computeNearestFromPointer(
      event.clientX,
      event.clientY,
      containerRect,
      scrollOffsets
    )
    const shortcutGroupTarget = getShortcutGroupTargetForDrag(
      item,
      state.dragOffset,
      nearestRow,
      nearestCol
    )
    if (shortcutGroupTarget) {
      setHoveredShortcutGroupId(shortcutGroupTarget.id)
      if (item.itemType === 'shortcut') {
        const groupMembers = shortcutGroupTarget.memberIds
          .map(id => items.find(candidate => candidate.id === id))
          .filter(
            (candidate): candidate is DesktopGridItem =>
              !!candidate && candidate.id !== item.id
          )
        const excludedGroupIds = shortcutGroups
          .filter(
            group =>
              group.id === shortcutGroupTarget.id ||
              group.memberIds.includes(item.id)
          )
          .map(group => group.id)
        const insertionPlan = getShortcutGroupInsertionPlan({
          members: groupMembers,
          item,
          occupied: [
            ...items.filter(candidate => candidate.id !== item.id),
            ...getGroupBoundaryObstacles(excludedGroupIds)
          ],
          cols,
          rows,
          dropPosition: { row: nearestRow, col: nearestCol },
          reorderItemId: shortcutGroupTarget.memberIds.includes(item.id)
            ? item.id
            : undefined,
          reorderItemPosition: item.position
        })
        updateShortcutGroupBoundsPreview(
          shortcutGroupTarget.id,
          insertionPlan,
          item
        )
        setDropPreview(
          insertionPlan
            ? {
                position: insertionPlan.target,
                size: { ...item.size },
                itemId: item.id
              }
            : null
        )
      } else {
        setDropPreview(null)
      }
      return
    }
    setHoveredShortcutGroupId(null)

    const target = computeTargetPosition({
      itemId: state.itemId,
      dragOffset: state.dragOffset,
      nearestRow,
      nearestCol
    })

    setDropPreview(target ? { position: target, size: { ...item.size } } : null)
  }

  const handleTouchPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') return

    const state = touchStateRef.current
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    if (!state || state.pointerId !== event.pointerId) {
      finalizeTouchDrag({ resetPreview: false })
      return
    }

    if (!state.started) {
      finalizeTouchDrag({ resetPreview: false })
      return
    }

    event.preventDefault()
    const containerRect = state.container?.getBoundingClientRect()
    if (!containerRect) {
      finalizeTouchDrag({ resetPreview: true })
      return
    }
    const scrollOffsets = {
      left: state.container?.scrollLeft ?? 0,
      top: state.container?.scrollTop ?? 0
    }

    const { nearestRow, nearestCol } = computeNearestFromPointer(
      event.clientX,
      event.clientY,
      containerRect,
      scrollOffsets
    )

    const folderTargetId = findFolderItemAtCell(nearestRow, nearestCol, [
      state.itemId
    ])
    if (folderTargetId && isShortcutItemId(state.itemId)) {
      void onFolderDrop(folderTargetId, [state.itemId])
      finalizeTouchDrag({ resetPreview: true })
      return
    }

    const draggedItem = items.find(item => item.id === state.itemId)
    const shortcutGroupTarget = draggedItem
      ? getShortcutGroupTargetForDrag(
          draggedItem,
          state.dragOffset,
          nearestRow,
          nearestCol
        )
      : null
    if (shortcutGroupTarget) {
      onShortcutGroupDrop(shortcutGroupTarget.id, [state.itemId], {
        row: nearestRow,
        col: nearestCol
      })
      finalizeTouchDrag({ resetPreview: true })
      return
    }

    const target = computeTargetPosition({
      itemId: state.itemId,
      dragOffset: state.dragOffset,
      nearestRow,
      nearestCol
    })

    onShortcutGroupLeave([state.itemId])
    applyMoveIfNeeded(state.itemId, target)
    finalizeTouchDrag({ resetPreview: true })
  }

  const handleTouchPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') return
    finalizeTouchDrag({ resetPreview: true })
  }

  return {
    activeItemId,
    handleDragStart,
    handleShortcutGroupDragStart,
    handleContainerDrop,
    handleDragOver,
    handleDragLeave,
    handleExternalPointerPreview,
    handleDragEnd,
    dropPreview,
    isExternalDragOver,
    handleTouchPointerDown,
    handleTouchPointerMove,
    handleTouchPointerEnd,
    handleTouchPointerCancel,
    isTouchReorderActive,
    isTouchEnvironment,
    computeExternalDropPositionFromPointer,
    getExternalFolderTargetAtPointer,
    hoveredFolderItemId,
    hoveredShortcutGroupId,
    shortcutGroupBoundsPreview,
    groupDropPreview,
    moveShortcutGroupBy
  }
}
